import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Package, Heart, MapPin, LogOut, User as UserIcon, Lock, Coins } from "lucide-react";
import { api, formatINR } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const Account = () => {
  const { customer, customerLogout, refreshCustomer, loading } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState("orders");
  const [profile, setProfile] = useState({ full_name: "", email: "", phone: "" });
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [pwSaving, setPwSaving] = useState(false);
  const [wallet, setWallet] = useState(null);

  useEffect(() => {
    if (loading) return; // wait for auth hydration before redirecting
    if (!customer) { navigate("/"); return; }
    setProfile({
      full_name: customer.full_name || "",
      email: customer.email || "",
      phone: customer.phone || "",
    });
    api.get("/orders/me").then(r => setOrders(r.data)).catch(() => {});
    api.get("/coins/me").then(r => setWallet(r.data)).catch(() => {});
  }, [customer, navigate, loading]);

  const saveProfile = async () => {
    try {
      await api.patch("/auth/customer/me", profile);
      await refreshCustomer();
      toast.success("Profile updated");
    } catch { toast.error("Could not update"); }
  };

  const changePassword = async () => {
    if (!pw.current_password || !pw.new_password) { toast.error("Fill in both passwords"); return; }
    if (pw.new_password.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    if (pw.new_password !== pw.confirm_password) { toast.error("New passwords don't match"); return; }
    setPwSaving(true);
    try {
      await api.post("/auth/customer/change-password", {
        current_password: pw.current_password,
        new_password: pw.new_password,
      });
      toast.success("Password changed successfully");
      setPw({ current_password: "", new_password: "", confirm_password: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not change password");
    } finally { setPwSaving(false); }
  };

  if (loading) return <div className="rieave-container py-24 text-center text-mauve">Loading…</div>;
  if (!customer) return null;

  return (
    <div className="rieave-container py-12 md:py-16" data-testid="account-page">
      <p className="eyebrow mb-2">My account</p>
      <h1 className="font-hero text-4xl md:text-5xl mb-10">
        Hello, <span className="serif-italic text-rose">{customer.full_name || "beautiful"}</span>
      </h1>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        <aside className="bg-white rounded-2xl border border-border p-5 h-fit">
          {[
            { k: "orders", l: "My orders", icon: Package },
            { k: "coins", l: "Coins", icon: Coins },
            { k: "wishlist", l: "Wishlist", icon: Heart, to: "/wishlist" },
            { k: "addresses", l: "Addresses", icon: MapPin },
            { k: "profile", l: "Profile", icon: UserIcon },
            { k: "password", l: "Password", icon: Lock },
          ].map(it => (
            <button
              key={it.k}
              onClick={() => it.to ? navigate(it.to) : setTab(it.k)}
              className={`flex items-center gap-3 w-full text-left px-3 py-2.5 rounded-lg font-label text-[12px] uppercase tracking-[0.22em] transition ${tab === it.k && !it.to ? "bg-cream text-charcoal" : "text-mauve hover:bg-cream hover:text-charcoal"}`}
              data-testid={`account-tab-${it.k}`}
            >
              <it.icon size={15} /> {it.l}
            </button>
          ))}
          <button
            onClick={() => { customerLogout(); navigate("/"); }}
            className="flex items-center gap-3 w-full text-left px-3 py-2.5 rounded-lg font-label text-[12px] uppercase tracking-[0.22em] text-mauve hover:bg-cream hover:text-rose"
            data-testid="account-logout"
          >
            <LogOut size={15} /> Sign out
          </button>
        </aside>

        <section className="lg:col-span-3">
          {tab === "orders" && (
            <div className="bg-white rounded-2xl border border-border p-6" data-testid="account-orders">
              <p className="eyebrow mb-4">Your orders</p>
              {orders.length === 0 ? (
                <p className="text-mauve">You haven't placed an order yet. <Link to="/shop" className="text-rose">Start shopping →</Link></p>
              ) : (
                <div className="space-y-4">
                  {orders.map(o => (
                    <div key={o.id} className="p-5 border border-border rounded-xl" data-testid={`order-${o.order_number}`}>
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-3">
                        <div>
                          <p className="font-cormorant text-xl">{o.order_number}</p>
                          <p className="font-label text-[11px] uppercase tracking-[0.22em] text-mauve">{new Date(o.created_at).toDateString()}</p>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-label text-[10px] uppercase tracking-[0.2em] px-3 py-1 rounded-full bg-lavender text-charcoal">{o.order_status}</span>
                          <span className={`font-label text-[10px] uppercase tracking-[0.2em] px-3 py-1 rounded-full ${o.payment_status === "paid" ? "bg-gold/40" : "bg-blush"} text-charcoal`}>{o.payment_status}</span>
                          <span className="font-cormorant text-xl">{formatINR(o.total)}</span>
                        </div>
                      </div>
                      <div className="flex gap-3 overflow-x-auto hide-scrollbar">
                        {o.items.map((it, i) => (
                          <div key={i} className="flex-shrink-0 flex items-center gap-3 bg-cream rounded-xl p-2 pr-4">
                            <div className="w-10 h-12 rounded bg-blush/40 overflow-hidden">{it.image && <img src={it.image} alt="" className="w-full h-full object-cover" />}</div>
                            <div>
                              <p className="text-sm font-cormorant leading-tight">{it.name}</p>
                              <p className="text-xs text-mauve">Qty {it.quantity}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === "coins" && (
            <div className="bg-white rounded-2xl border border-border p-6" data-testid="account-coins">
              <p className="eyebrow mb-4">{wallet?.settings?.coin_name || "Rieave Coins"}</p>
              <div className="rounded-2xl bg-gradient-to-br from-rose/15 via-blush/40 to-lavender/40 p-6 mb-6">
                <p className="font-label text-[11px] uppercase tracking-[0.22em] text-mauve mb-1">Available balance</p>
                <div className="flex items-end gap-3">
                  <p className="font-hero text-5xl text-charcoal" data-testid="coins-balance">
                    {wallet?.balance ?? 0}
                  </p>
                  <p className="text-mauve mb-2">coins ≈ {formatINR(wallet?.value_inr || 0)}</p>
                </div>
                <p className="text-sm text-charcoal/80 mt-3">
                  Use up to <strong>{wallet?.settings?.max_redeem_pct ?? 10}%</strong> of any order at checkout.
                  Earn more on every delivered order.
                </p>
              </div>

              <p className="eyebrow mb-3">Recent activity</p>
              {(!wallet || wallet.transactions.length === 0) ? (
                <p className="text-mauve text-sm">No activity yet — your earned coins will show up here.</p>
              ) : (
                <div className="divide-y divide-border">
                  {wallet.transactions.map(t => (
                    <div key={t.id} className="py-3 flex items-center justify-between" data-testid={`coin-txn-${t.id}`}>
                      <div className="min-w-0">
                        <p className="text-sm text-charcoal truncate">{t.note || t.kind}</p>
                        <p className="text-xs text-mauve">{new Date(t.created_at).toLocaleDateString()} · {t.kind}</p>
                      </div>
                      <span className={`font-cormorant text-lg ${t.delta >= 0 ? "text-emerald-700" : "text-rose"}`}>
                        {t.delta >= 0 ? "+" : ""}{t.delta}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === "addresses" && (
            <div className="bg-white rounded-2xl border border-border p-6" data-testid="account-addresses">
              <p className="eyebrow mb-4">Saved addresses</p>
              {(customer.addresses || []).length === 0 ? (
                <p className="text-mauve">No addresses saved yet. Your next order will save it automatically.</p>
              ) : (
                <div className="grid md:grid-cols-2 gap-4">
                  {customer.addresses.map(a => (
                    <div key={a.id} className="p-4 border border-border rounded-xl">
                      <p className="font-cormorant text-lg">{a.full_name}</p>
                      <p className="text-sm text-charcoal/80">{a.line1}{a.line2 ? `, ${a.line2}` : ""}</p>
                      <p className="text-sm text-charcoal/80">{a.city}, {a.state} {a.postal_code}</p>
                      <p className="text-sm text-mauve">{a.country} · {a.phone}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === "profile" && (
            <div className="bg-white rounded-2xl border border-border p-6" data-testid="account-profile">
              <p className="eyebrow mb-4">Your profile</p>
              <div className="grid md:grid-cols-2 gap-4">
                <Input placeholder="Full name" value={profile.full_name} onChange={e => setProfile({ ...profile, full_name: e.target.value })} className="h-11 rounded-xl" />
                <Input placeholder="Email" type="email" value={profile.email || ""} onChange={e => setProfile({ ...profile, email: e.target.value })} className="h-11 rounded-xl" />
                <Input placeholder="Phone" value={profile.phone || ""} onChange={e => setProfile({ ...profile, phone: e.target.value })} className="h-11 rounded-xl md:col-span-2" />
              </div>
              <button className="btn-primary mt-5" onClick={saveProfile} data-testid="profile-save">Save changes</button>
            </div>
          )}

          {tab === "password" && (
            <div className="bg-white rounded-2xl border border-border p-6" data-testid="account-password">
              <p className="eyebrow mb-2">Change password</p>
              <p className="text-sm text-mauve mb-5">Use a password you don't reuse on other sites.</p>
              <div className="space-y-4 max-w-md">
                <Input
                  type="password" placeholder="Current password" value={pw.current_password}
                  onChange={e => setPw({ ...pw, current_password: e.target.value })}
                  className="h-11 rounded-xl" data-testid="password-current"
                />
                <Input
                  type="password" placeholder="New password (6+ chars)" value={pw.new_password}
                  onChange={e => setPw({ ...pw, new_password: e.target.value })}
                  className="h-11 rounded-xl" data-testid="password-new"
                />
                <Input
                  type="password" placeholder="Confirm new password" value={pw.confirm_password}
                  onChange={e => setPw({ ...pw, confirm_password: e.target.value })}
                  className="h-11 rounded-xl" data-testid="password-confirm"
                />
                <button
                  className="btn-primary"
                  onClick={changePassword}
                  disabled={pwSaving}
                  data-testid="password-save"
                >
                  {pwSaving ? "Updating…" : "Update password"}
                </button>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default Account;
